"use client";

import { GlowButton, Nav, SiteFooter } from "@/apps/charm/components/marketing";
import { useGsapStagger } from "@/apps/charm/components/marketing/use-gsap";

export function ContactPage() {
  const ref = useGsapStagger(0.15);

  return (
    <main className="bg-v2-bg min-h-screen">
      <Nav />

      <section className="v2-dark-textured bg-v2-black relative overflow-hidden pt-32 pb-24 md:pt-48 md:pb-32">
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,10,8,0.3) 0%, rgba(10,10,8,0.9) 100%), " +
              "radial-gradient(circle at 50% 100%, rgba(201,168,76,0.15) 0%, transparent 60%)",
          }}
        />

        <div className="relative z-10 mx-auto flex max-w-5xl flex-col items-center gap-6 px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-3 rounded-full border border-v2-gold/20 bg-v2-gold/5 px-4 py-1.5 backdrop-blur-md mb-4">
            <span className="flex h-2 w-2 rounded-full bg-v2-gold animate-pulse"></span>
            <span className="text-xs font-mono tracking-widest text-v2-gold uppercase">
              Contact Us
            </span>
          </div>

          <h1 className="font-heading text-5xl md:text-7xl lg:text-[80px] font-normal leading-tight tracking-tight text-v2-cream">
            Let&apos;s talk.
          </h1>
          <p className="max-w-xl text-lg md:text-xl font-light leading-relaxed text-v2-cream/70 mt-4">
            Whether you have a question about coverage, need help with an
            existing policy, or just want to understand your options—we&apos;re
            here.
          </p>
        </div>
      </section>

      <section className="v2-light-textured py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <div ref={ref} className="flex flex-col gap-6 max-w-2xl mx-auto">
            <ContactCard
              label="EMAIL US"
              title="contact@goldfinch.com"
              subtitle="We respond within 1 business day."
              icon={
                <svg
                  className="w-6 h-6 text-v2-gold"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              }
            />
            <div className="mt-8 rounded-3xl border border-v2-border bg-white p-8 md:p-12 shadow-xl shadow-v2-black/5 text-center flex flex-col items-center">
              <span className="inline-flex items-center rounded-full border border-v2-gold/30 bg-v2-gold/5 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-v2-gold mb-6">
                Not Sure Where to Start?
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-normal text-v2-text mb-4">
                Take our 2-minute quiz.
              </h2>
              <p className="text-base md:text-lg text-v2-text-muted mb-10 max-w-xl">
                We&apos;ll tell you what coverage would be a good fit—no phone
                call required.
              </p>
              <GlowButton href="/quiz" className="px-12 py-6 text-lg">
                Calculate Coverage
              </GlowButton>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}

function ContactCard(props: {
  label: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="group relative flex flex-col rounded-3xl border border-v2-border bg-white p-8 transition-all duration-500 hover:border-v2-gold/30 hover:shadow-xl hover:-translate-y-1">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-v2-bg-accent border border-v2-border mb-8 text-v2-text transition-colors group-hover:bg-v2-gold/10 group-hover:border-v2-gold/20">
        {props.icon}
      </div>
      <div className="mt-auto">
        <span className="font-mono text-[10px] tracking-widest text-v2-text-faint uppercase block mb-3">
          {props.label}
        </span>
        <h3 className="font-heading text-2xl md:text-3xl font-normal text-v2-text mb-2 transition-colors duration-300 group-hover:text-v2-gold">
          {props.title}
        </h3>
        <p className="text-sm text-v2-text-muted leading-relaxed">
          {props.subtitle}
        </p>
      </div>
    </div>
  );
}
